import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO 
        title="Privacy Policy"
        description="Read the Privacy Policy for AMP Predict. Learn how we collect, use, and protect your personal information on our prediction market platform."
        keywords="privacy policy, data protection, AMP Predict privacy, user data, ANJO LC"
        url="https://amppredict.com/privacy"
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-[#3B82F6]/20 rounded-xl">
            <Shield className="h-6 w-6 text-[#3B82F6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Privacy Policy</h1>
            <p className="text-gray-400 text-sm">Effective Date: 01/31/2026</p>
          </div>
        </div>

        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8">
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 mb-2">
              <strong>Company:</strong> ANJO LC ("ANJO", "we", "us")
            </p>
            <p className="text-gray-300 mb-6">
              <strong>Contact:</strong> <a href="mailto:amp@anjolc.com" className="text-[#3B82F6] hover:underline">amp@anjolc.com</a>
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">1) What this policy covers</h2>
              <p className="text-gray-300">
                This Privacy Policy explains how ANJO LC collects, uses, and protects personal information when you visit our website, contact us, or use our subscription-based services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">2) Information we collect</h2>
              <p className="text-gray-300 mb-4">We collect information in three main ways:</p>
              
              <h3 className="text-lg font-medium text-white mb-2">A) Information you provide to us</h3>
              <p className="text-gray-300 mb-4">
                We may collect your name, email address, phone number (optional), organization name, and message content submitted through our contact forms. In addition, any information you include in emails sent to amp@anjolc.com may be collected.
              </p>

              <h3 className="text-lg font-medium text-white mb-2">B) Billing and subscription information (Stripe)</h3>
              <p className="text-gray-300 mb-4">
                Payments and subscriptions are processed through Stripe. We may receive and store limited billing-related information such as Stripe customer ID, subscription ID, invoice IDs, payment status, plan type, and transaction timestamps. Refund request details and outcomes are retained for audit and customer support purposes. We do not store your full card number; card details are handled by Stripe.
              </p>

              <h3 className="text-lg font-medium text-white mb-2">C) Website usage data (analytics and technical data)</h3>
              <p className="text-gray-300">
                When you browse the site, we may automatically collect information such as your IP address (or approximate location derived from it), device type, browser type, pages visited, time on page, referring/exit pages, and cookies or similar tracking technologies (see Section 6).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">3) How we use your information</h2>
              <p className="text-gray-300">We use information to:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 mt-2">
                <li>Respond to inquiries and provide requested information</li>
                <li>Provide, maintain, and improve services</li>
                <li>Manage subscriptions, invoicing, and billing support</li>
                <li>Review and process refund requests (including fraud and abuse prevention)</li>
                <li>Monitor site performance and troubleshoot issues</li>
                <li>Comply with legal obligations and enforce our policies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">4) How we share information</h2>
              <p className="text-gray-300 mb-4">
                We do not sell personal information. We may share information with:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Service providers that help us run the website and services (e.g., hosting, analytics, email delivery)</li>
                <li>Stripe for payments, subscriptions, invoicing, and refunds</li>
                <li>Professional advisors (legal, accounting) when needed</li>
                <li>Authorities if required to comply with law or protect rights and safety</li>
              </ul>
              <p className="text-gray-300 mt-4">
                We only share information as necessary to provide services or meet obligations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">5) Data retention</h2>
              <p className="text-gray-300 mb-4">We retain information only as long as necessary for:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Responding to requests and providing services</li>
                <li>Billing, refunds, audits, and recordkeeping</li>
                <li>Legal compliance and dispute resolution</li>
              </ul>
              <p className="text-gray-300 mt-4">
                Refund requests and billing records may be retained longer for audit and reconciliation purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">6) Cookies and tracking</h2>
              <p className="text-gray-300 mb-4">We may use cookies or similar technologies to:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Keep the site functioning properly</li>
                <li>Understand how visitors use the site (analytics)</li>
                <li>Improve site performance and user experience</li>
              </ul>
              <p className="text-gray-300 mt-4">
                You can control cookies through your browser settings. Some features may not work correctly if cookies are disabled.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">7) Security</h2>
              <p className="text-gray-300">
                We use reasonable administrative, technical, and organizational safeguards to protect information. No system is perfect, but we take data protection seriously—especially for billing and client communications.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">8) International visitors</h2>
              <p className="text-gray-300">
                If you access our site from outside the United States, your information may be processed in locations where our service providers operate.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">9) Children's privacy</h2>
              <p className="text-gray-300">
                Our services and website are not intended for children under 13, and we do not knowingly collect personal information from children.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">10) Your choices</h2>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>You may request access, correction, or deletion of certain information by emailing amp@anjolc.com</li>
                <li>You may opt out of non-essential emails at any time (where applicable)</li>
                <li>Some information may need to be retained for billing, legal, or compliance reasons</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">11) Changes to this policy</h2>
              <p className="text-gray-300">
                We may update this Privacy Policy from time to time. The latest version will be posted on this page with an updated effective date.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
